import type { JSX, ReactNode } from 'react';
import { makeStyles, tokens } from '@fluentui/react-components';

/**
 * Pastille « non lu » posée en BADGE sur le coin supérieur droit de l'icône.
 *
 * POURQUOI SUR L'ICÔNE, ET NON À CÔTÉ. La pastille occupait auparavant une
 * colonne à elle, avant l'icône. Cette colonne devait alors être RÉSERVÉE sur
 * toutes les lignes — dossiers et espaces compris, qui n'ont pourtant aucun état
 * de lecture — sans quoi les icônes des différentes natures ne s'alignaient plus
 * dans une liste où elles se suivent. On payait donc, sur chaque ligne, huit
 * pixels de vide pour une information qui ne concerne que les mails. En badge, il
 * n'y a plus rien à réserver : l'alignement des icônes est acquis par
 * construction, et la pastille désigne enfin l'objet dont elle parle.
 *
 * POSÉE SUR LE GLYPHE, coin supérieur droit, sans débord (`top`/`right` à zéro).
 *
 * ⚠ CONTRAINTE SUR L'ENFANT : il ne doit porter AUCUNE marge de fin. Le repère de
 * la pastille est la boîte du conteneur, laquelle englobe les marges de l'enfant :
 * un `marginRight` sur l'icône décale donc la pastille d'autant vers la droite, et
 * elle flotte dans l'espace entre l'icône et le libellé au lieu de la toucher.
 * C'est exactement ce qui est arrivé dans l'arbre — dont l'icône portait un
 * `marginRight` de 2 px là où la page détail n'en avait pas —, et c'est pourquoi la
 * mesure faite sur la page détail ne l'a pas vu. La marge a été retirée : l'écart
 * avec le libellé est désormais celui de la ligne (`gap`), pour toutes les vues.
 *
 * RIEN DU TOUT quand le mail est lu : pas même un conteneur. Ces listes comptent
 * des centaines de lignes, dont la quasi-totalité est lue ; leur ajouter un nœud
 * DOM chacune pour un badge absent serait payé sans contrepartie.
 *
 * L'infobulle est NATIVE (`title`) et non un `Tooltip` Fluent : elle couvre toute
 * l'icône plutôt que les six pixels de la pastille, et les trois vues qui
 * l'appellent l'obtiennent identiquement — auparavant deux d'entre elles n'en
 * avaient aucune.
 */
const useStyles = makeStyles({
  wrap: {
    position: 'relative',
    display: 'inline-flex',
    // La pastille dépasse du cadre : sans ceci le conteneur pourrait être
    // comprimé par le flex de la ligne et la rogner.
    flexShrink: 0,
    lineHeight: 0,
  },
  dot: {
    position: 'absolute',
    top: 0,
    right: 0,
    width: '6px',
    height: '6px',
    borderRadius: '50%',
    backgroundColor: tokens.colorBrandBackground,
    // La ligne entière est cliquable (ouvre le mail) : le badge ne doit pas
    // intercepter ce clic, ni le survol qui révèle le bouton ⋯.
    pointerEvents: 'none',
  },
});

interface Props {
  /**
   * `true` UNIQUEMENT sur un mail dont on sait qu'il n'est pas lu.
   *
   * Les appelants comparent `isRead === false` strictement : `undefined` signifie
   * « le back-end ne le dit pas » (dossier, espace, ou API antérieure à la
   * migration 0027), et le confondre avec « non lu » barbouillerait de bleu une
   * liste entière sur un simple retard de déploiement.
   */
  unread: boolean;
  children: ReactNode;
}

export function UnreadBadge({ unread, children }: Props): JSX.Element {
  const styles = useStyles();
  if (!unread) return <>{children}</>;
  return (
    <span className={styles.wrap} title="Non lu — vous ne l’avez pas encore ouvert">
      {children}
      <span className={styles.dot} />
    </span>
  );
}
